import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, ExternalLink, Check, X, Pencil, Clock3, RefreshCw, Star, Send, Mail } from "lucide-react";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { MemberLink } from "@/components/build/MemberLink";

// Creative Radar Phase 1 (2026-08) — founder-approved after a Phase 0 audit. Everything on
// this page enforces the spec's rules structurally, not just by convention: candidates are
// pre-filtered ACTIVE members only (server-side), AI drafts copy the editor can freely
// override but never sees/produces numbers (the stats block is separate, deterministic), and
// NOTHING reaches The Floor until the explicit Publish action below.

const CATEGORIES = ["New Work", "BTS", "New Release", "Collaboration", "Craft", "Recognition"];

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-hairline bg-surface px-4 py-3" data-testid={`radar-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-2xl font-semibold text-ink">{value}</div>
      <div className="text-xs text-ink-soft mt-0.5">{label}</div>
    </div>
  );
}

function CandidateCard({ c, onDecide, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [headline, setHeadline] = useState(c.edited_headline || c.ai_headline || "");
  const [summary, setSummary] = useState(c.edited_summary || c.ai_summary || "");
  const included = c.editor_status === "included";
  const skipped = c.editor_status === "skipped";

  const save = () => {
    onEdit(c.id, headline, summary);
    setEditing(false);
  };

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${included ? "border-brand bg-brand/5" : skipped ? "border-hairline bg-surface2 opacity-60" : "border-hairline bg-surface"}`}
      data-testid={`radar-candidate-${c.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ink">{c.freelancer_name}</span>
            <Badge variant="outline" className="text-[10px]" data-testid={`radar-category-${c.id}`}>{c.category}</Badge>
            {c.is_manual && <Badge className="text-[10px] bg-brand/20 text-ink border-0">Manual</Badge>}
            {c.rotation_deprioritized && (
              <span className="inline-flex items-center gap-1 text-[10px] text-ink-soft" title="Featured recently — deprioritized, not excluded">
                <Clock3 className="h-3 w-3" /> recently featured
              </span>
            )}
          </div>
          <div className="text-xs text-ink-soft mt-0.5">{c.role || "—"}{c.city ? ` · ${c.city}` : ""} · {c.rotation_times_featured || 0}x featured before</div>
        </div>
        {c.source_link && (
          <a href={c.source_link} target="_blank" rel="noreferrer" className="shrink-0 text-ink-soft hover:text-brand" data-testid={`radar-source-link-${c.id}`}>
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <input value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={80}
            className="w-full rounded-lg border border-hairline bg-surface2 px-3 py-1.5 text-sm text-ink" data-testid={`radar-edit-headline-${c.id}`} />
          <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={260} rows={2}
            className="text-sm" data-testid={`radar-edit-summary-${c.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} data-testid={`radar-save-edit-${c.id}`}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <div className="text-sm font-medium text-ink">{c.edited_headline || c.ai_headline}</div>
          <p className="text-sm text-ink-soft mt-1">{c.edited_summary || c.ai_summary}</p>
          {c.why_featured && <p className="text-xs text-faint mt-1 italic">"{c.why_featured}"</p>}
        </div>
      )}

      {!editing && (
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant={included ? "default" : "outline"} onClick={() => onDecide(c.id, "included")} data-testid={`radar-include-${c.id}`}>
            <Check className="h-3.5 w-3.5 mr-1" /> Include
          </Button>
          <Button size="sm" variant={skipped ? "secondary" : "outline"} onClick={() => onDecide(c.id, "skipped")} data-testid={`radar-skip-${c.id}`}>
            <X className="h-3.5 w-3.5 mr-1" /> Skip
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid={`radar-edit-${c.id}`}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        </div>
      )}
    </div>
  );
}

function ManualAddForm({ freelancers, onAdd }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [quote, setQuote] = useState("");
  const [workLink, setWorkLink] = useState("");
  const [category, setCategory] = useState("Recognition");

  const matches = query.trim().length >= 2 && !picked
    ? freelancers.filter((f) => f.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 6)
    : [];

  const submit = () => {
    if (!picked) return toast.error("Pick a member first");
    onAdd({ freelancer_id: picked.id, quote, work_link: workLink, category });
    setQuery(""); setPicked(null); setQuote(""); setWorkLink(""); setCategory("Recognition");
  };

  return (
    <div className="rounded-xl border border-dashed border-hairline p-4 space-y-2">
      <div className="text-sm font-medium text-ink">Add a member manually</div>
      <div className="relative">
        <input
          placeholder="Search member by name…" value={picked ? picked.name : query}
          onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
          className="w-full rounded-lg border border-hairline bg-surface2 px-3 py-1.5 text-sm text-ink"
          data-testid="radar-manual-search-input"
        />
        {matches.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-hairline bg-surface shadow-lg max-h-48 overflow-auto">
            {matches.map((f) => (
              <button key={f.id} onClick={() => setPicked(f)} data-testid={`radar-manual-pick-${f.id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-surface2 text-ink">
                {f.name} <span className="text-ink-soft text-xs">— {f.primary_role || "—"}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <input placeholder="Work link (optional)" value={workLink} onChange={(e) => setWorkLink(e.target.value)}
        className="w-full rounded-lg border border-hairline bg-surface2 px-3 py-1.5 text-sm text-ink" data-testid="radar-manual-work-link" />
      <Textarea placeholder="Why they're featured (a real quote or note)" value={quote} onChange={(e) => setQuote(e.target.value)} rows={2}
        className="text-sm" data-testid="radar-manual-quote" />
      <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="radar-manual-category"
        className="rounded-lg border border-hairline bg-surface2 px-3 py-1.5 text-sm text-ink">
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <Button size="sm" onClick={submit} disabled={!picked} data-testid="radar-manual-add-submit">Add candidate</Button>
    </div>
  );
}

export function CotwCandidateCard({ c, selected, onSelect }) {
  return (
    <div className={`rounded-xl border p-4 ${selected ? "border-brand bg-brand/5" : "border-hairline bg-surface"}`} data-testid={`cotw-candidate-${c.id}`}>
      <div className="flex items-start gap-3">
        <ProfileAvatar
          freelancerId={c.freelancer_id}
          hasPhoto={c.has_photo}
          alt=""
          className="h-12 w-12 rounded-full object-cover shrink-0 border border-hairline"
          testId={`cotw-candidate-avatar-${c.id}`}
          fallback={<div className="h-12 w-12 rounded-full bg-brand/15 text-ink flex items-center justify-center text-sm font-semibold shrink-0">{(c.name || "?")[0]}</div>}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-ink">{c.name}</span>
            {c.editorial_status === "published" && <Badge className="text-[10px] bg-brand text-ink border-0">Published</Badge>}
            {c.editorial_status === "included" && <Badge variant="outline" className="text-[10px]">Included</Badge>}
            {c.editorial_status === "approved" && <Badge variant="outline" className="text-[10px]">Approved</Badge>}
          </div>
          <div className="text-xs text-ink-soft mt-0.5">{c.primary_role || "—"}{c.city ? ` · ${c.city}` : ""}</div>
          <div className="text-xs text-ink-soft mt-0.5">Readiness: {c.profile_readiness || "—"} · Featured {c.feature_count || 0}x before{c.last_featured ? ` (last ${c.last_featured})` : ""}</div>
        </div>
        <MemberLink freelancerId={c.freelancer_id} className="shrink-0 text-ink-soft hover:text-brand" testId={`cotw-view-njm-profile-${c.id}`}>
          <ExternalLink className="h-4 w-4" />
        </MemberLink>
      </div>
      {c.suggested_feature_angle && <p className="text-xs text-ink-soft mt-2.5 italic">"{c.suggested_feature_angle}"</p>}
      {c.suggested_fun_question && <p className="text-xs text-faint mt-1">Fun question idea: {c.suggested_fun_question}</p>}
      {(c.recent_njm_activity || c.recent_linkedin_activity) && (
        <div className="text-[11px] text-faint mt-1.5 space-y-0.5">
          {c.recent_njm_activity && <div>NJM: {c.recent_njm_activity}</div>}
          {c.recent_linkedin_activity && <div>LinkedIn: {c.recent_linkedin_activity}</div>}
        </div>
      )}
      <div className="mt-3">
        <Button size="sm" variant={selected ? "secondary" : "outline"} onClick={() => onSelect(c.id)} data-testid={`cotw-select-btn-${c.id}`}>
          {selected ? "Selected" : "Select creator"}
        </Button>
      </div>
    </div>
  );
}

export function CotwEditor({ candidate, onSave, onApprove, onInclude, onPublish, busy, onDraftOutreach, onSendOutreach, onUseReply }) {
  const [introCopy, setIntroCopy] = useState(candidate.intro_copy || "");
  const [introText, setIntroText] = useState(candidate.intro_text || "");
  const [funQuestion, setFunQuestion] = useState(candidate.fun_question || candidate.suggested_fun_question || "");
  const [funAnswer, setFunAnswer] = useState(candidate.fun_answer || "");
  const firstName = (candidate.name || "this creator").split(" ")[0];
  const status = candidate.editorial_status;

  return (
    <div className="rounded-xl border border-brand/40 bg-surface p-4 space-y-3" data-testid="cotw-editor">
      <div className="text-sm font-semibold text-ink">Final preview — {candidate.name}</div>

      <StageTracker stage={candidate.stage} />

      {candidate.latest_reply_text && (
        <div className="rounded-lg border border-hairline bg-surface2 p-3 space-y-2" data-testid="cotw-latest-reply">
          <div className="text-xs font-semibold text-ink-soft">{firstName}'s latest reply in Notes</div>
          <p className="text-sm text-ink whitespace-pre-wrap">{candidate.latest_reply_text}</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => { setIntroText(candidate.latest_reply_text); onUseReply(candidate.id, { intro_text: candidate.latest_reply_text }); }} data-testid="cotw-use-as-introduction-btn">
              Use as introduction
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setFunAnswer(candidate.latest_reply_text); onUseReply(candidate.id, { fun_answer: candidate.latest_reply_text }); }} data-testid="cotw-use-as-answer-btn">
              Use as answer
            </Button>
          </div>
        </div>
      )}

      <div>
        <label className="text-xs text-ink-soft">NJM introductory copy</label>
        <Textarea value={introCopy} onChange={(e) => setIntroCopy(e.target.value)} rows={2} className="text-sm mt-1" data-testid="cotw-intro-copy-input" />
      </div>
      <div>
        <label className="text-xs text-ink-soft">From {firstName}</label>
        <Textarea value={introText} onChange={(e) => setIntroText(e.target.value)} rows={2} placeholder="Paste the creator's introduction…" className="text-sm mt-1" data-testid="cotw-intro-text-input" />
      </div>
      <div>
        <label className="text-xs text-ink-soft">We asked {firstName}</label>
        <input value={funQuestion} onChange={(e) => setFunQuestion(e.target.value)}
          className="w-full rounded-lg border border-hairline bg-surface2 px-3 py-1.5 text-sm text-ink mt-1" data-testid="cotw-fun-question-input" />
      </div>
      <div>
        <label className="text-xs text-ink-soft">{firstName}'s answer</label>
        <Textarea value={funAnswer} onChange={(e) => setFunAnswer(e.target.value)} rows={2} placeholder="Paste the creator's answer…" className="text-sm mt-1" data-testid="cotw-fun-answer-input" />
      </div>

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <Button size="sm" variant="outline" disabled={busy}
          onClick={() => onSave({ intro_copy: introCopy, intro_text: introText, fun_question: funQuestion, fun_answer: funAnswer })}
          data-testid="cotw-edit-save-btn">
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onDraftOutreach(candidate.id)} data-testid="cotw-draft-outreach-btn">
          <Mail className="h-3.5 w-3.5 mr-1" /> Draft outreach
        </Button>
        <Button size="sm" variant={status === "candidate" ? "default" : "outline"} disabled={busy || status !== "candidate"} onClick={onApprove} data-testid="cotw-approve-btn">
          <Check className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button size="sm" variant={status === "approved" ? "default" : "outline"} disabled={busy || status !== "approved"} onClick={onInclude} data-testid="cotw-include-btn">
          Include in edition
        </Button>
        <Button size="sm" variant={status === "included" ? "default" : "outline"} disabled={busy || status !== "included"} onClick={onPublish} data-testid="cotw-publish-btn">
          <Send className="h-3.5 w-3.5 mr-1" /> Publish
        </Button>
      </div>
    </div>
  );
}

const STAGE_LABELS = { selected: "Selected", outreach_sent: "Outreach sent", response_received: "Response received", ready: "Ready" };
const STAGE_ORDER = ["selected", "outreach_sent", "response_received", "ready"];

function StageTracker({ stage }) {
  if (!stage) return null;
  const idx = STAGE_ORDER.indexOf(stage);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="cotw-stage-tracker">
      {STAGE_ORDER.map((s, i) => (
        <span key={s} data-testid={`cotw-stage-${s}`}
          className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${i <= idx ? "border-brand bg-brand/15 text-ink" : "border-hairline text-ink-soft"}`}>
          {STAGE_LABELS[s]}
        </span>
      ))}
    </div>
  );
}

export function OutreachModal({ candidate, draft, onClose, onSend, sending }) {
  const [emailSubject, setEmailSubject] = useState(draft.email_subject || "");
  const [funQuestion, setFunQuestion] = useState(draft.fun_question || "");
  const [noteText, setNoteText] = useState(draft.note_text || "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="cotw-outreach-modal">
      <div className="w-full max-w-lg rounded-xl bg-paper p-5 space-y-4 max-h-[85vh] overflow-y-auto">
        <div className="text-sm font-semibold text-ink">Draft outreach — {candidate.name}</div>
        {!draft.has_email && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="cotw-outreach-no-email-warning">
            This creator has no email on file — only the NJM Build Note will be sent.
          </div>
        )}
        <div>
          <label className="text-xs text-ink-soft">Email subject</label>
          <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
            className="w-full rounded-lg border border-hairline bg-surface2 px-3 py-1.5 text-sm text-ink mt-1" data-testid="cotw-outreach-subject-input" />
        </div>
        <div>
          <label className="text-xs text-ink-soft">Fun question (used in the email)</label>
          <Textarea value={funQuestion} onChange={(e) => setFunQuestion(e.target.value)} rows={2} className="text-sm mt-1" data-testid="cotw-outreach-fun-question-input" />
        </div>
        <div>
          <label className="text-xs text-ink-soft">NJM Build Note</label>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4} className="text-sm mt-1" data-testid="cotw-outreach-note-input" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="cotw-outreach-cancel-btn">Cancel</Button>
          <Button size="sm" disabled={sending} onClick={() => onSend({ email_subject: emailSubject, fun_question: funQuestion, note_text: noteText })} data-testid="cotw-outreach-send-btn">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            Send outreach
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CreatorOfTheWeekSection() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [outreachDraft, setOutreachDraft] = useState(null); // { candidateId, draft }
  const [sendingOutreach, setSendingOutreach] = useState(false);

  const loadStatus = useCallback(() => {
    api.get("/v1/creative-radar/creator-engagement/status").then((r) => setSyncStatus(r.data)).catch(() => {});
  }, []);

  const loadCandidates = useCallback(() => {
    setLoading(true);
    api.get("/v1/creative-radar/creator-of-the-week").then((r) => setCandidates(r.data.items || [])).catch(() => toast.error("Couldn't load Creator of the Week candidates")).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadStatus(); loadCandidates(); }, [loadStatus, loadCandidates]);

  const sync = () => {
    setSyncing(true);
    api.post("/v1/creative-radar/sync-creator-engagement")
      .then((r) => { toast.success(`Synced — ${r.data.cotw_count} Creator of the Week, ${r.data.around_network_count} Around the Network`); loadStatus(); loadCandidates(); })
      .catch(() => toast.error("Couldn't sync the Creator Engagement tracker"))
      .finally(() => setSyncing(false));
  };

  const selected = candidates.find((c) => c.selected);

  const selectCandidate = (id) => {
    setBusy(true);
    api.post(`/v1/creative-radar/creator-of-the-week/${id}/select`)
      .then(() => loadCandidates())
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't select this creator"))
      .finally(() => setBusy(false));
  };

  const saveEdit = (id, fields) => {
    setBusy(true);
    api.patch(`/v1/creative-radar/creator-of-the-week/${id}`, fields)
      .then(() => { toast.success("Saved"); loadCandidates(); })
      .catch(() => toast.error("Couldn't save"))
      .finally(() => setBusy(false));
  };

  const approve = (id) => {
    setBusy(true);
    api.post(`/v1/creative-radar/creator-of-the-week/${id}/approve`)
      .then(() => loadCandidates())
      .catch((e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail?.code || "Couldn't approve"))
      .finally(() => setBusy(false));
  };

  const include = (id) => {
    setBusy(true);
    api.post(`/v1/creative-radar/creator-of-the-week/${id}/include`)
      .then(() => loadCandidates())
      .catch(() => toast.error("Couldn't include this edition"))
      .finally(() => setBusy(false));
  };

  const publish = (id) => {
    if (!window.confirm("Publish this Creator of the Week to The Floor? This can't be undone.")) return;
    setBusy(true);
    api.post(`/v1/creative-radar/creator-of-the-week/${id}/publish`)
      .then(() => { toast.success("Published to The Floor"); loadCandidates(); })
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't publish"))
      .finally(() => setBusy(false));
  };

  const draftOutreach = (id) => {
    setBusy(true);
    api.post(`/v1/creative-radar/creator-of-the-week/${id}/draft-outreach`)
      .then((r) => setOutreachDraft({ candidateId: id, draft: r.data }))
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't draft outreach"))
      .finally(() => setBusy(false));
  };

  const sendOutreach = (fields) => {
    if (!outreachDraft) return;
    setSendingOutreach(true);
    api.post(`/v1/creative-radar/creator-of-the-week/${outreachDraft.candidateId}/send-outreach`, fields)
      .then((r) => {
        if (r.data?.ok === false) {
          // Fix pass E (2026-11): a blocked/failed email send no longer reports "Outreach
          // sent" — the backend now returns ok:false with a clear reason instead.
          toast.error(r.data.email_status_message || "Email not sent: blocked");
        } else {
          toast.success("Outreach sent");
        }
        setOutreachDraft(null);
        loadCandidates();
      })
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't send outreach"))
      .finally(() => setSendingOutreach(false));
  };

  const useReply = (id, fields) => { saveEdit(id, fields); };

  return (
    <div className="rounded-2xl border border-hairline bg-surface p-5 space-y-4" data-testid="cotw-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-brand" />
          <h2 className="text-lg font-semibold text-ink">Creator of the Week</h2>
        </div>
        <div className="flex items-center gap-3">
          {syncStatus?.last_synced_at && (
            <span className="text-xs text-ink-soft" data-testid="cotw-last-synced">
              Last synced {new Date(syncStatus.last_synced_at).toLocaleString()} · {syncStatus.cotw_count} CotW, {syncStatus.around_network_count} Around the Network
            </span>
          )}
          <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="cotw-sync-btn">
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Sync Creator Engagement
          </Button>
        </div>
      </div>

      {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-ink-soft" /></div>}

      {!loading && candidates.length === 0 && (
        <EmptyState title="No Creator of the Week candidates yet" hint="Sync the Creator Engagement tracker to pull in candidates." testId="cotw-empty-state" />
      )}

      {!loading && candidates.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {candidates.map((c) => (
            <CotwCandidateCard key={c.id} c={c} selected={c.selected} onSelect={selectCandidate} />
          ))}
        </div>
      )}

      {selected && (
        <CotwEditor candidate={selected} onSave={(f) => saveEdit(selected.id, f)} onApprove={() => approve(selected.id)} onInclude={() => include(selected.id)} onPublish={() => publish(selected.id)} busy={busy}
          onDraftOutreach={draftOutreach} onSendOutreach={sendOutreach} onUseReply={useReply} />
      )}

      {outreachDraft && (
        <OutreachModal candidate={selected} draft={outreachDraft.draft} sending={sendingOutreach} onClose={() => setOutreachDraft(null)} onSend={sendOutreach} />
      )}
    </div>
  );
}

// =================================================================================================
// Around the Network (Wave 4, 2026-11)
// =================================================================================================

const AN_STATUS_LABELS = { pending: "Pending", included: "Included", skipped: "Skipped", removed: "Removed", published: "Published" };

export function AroundNetworkCard({ c, onEditDraft, onDecide, onMoveUp, onMoveDown, busy }) {
  const [draft, setDraft] = useState(c.draft_text || c.tracker_draft || "");
  const activityText = c.activity_source === "njm" ? c.recent_njm_activity : c.activity_source === "linkedin" ? c.recent_linkedin_activity : null;

  return (
    <div className="rounded-xl border border-hairline bg-surface p-4 space-y-2.5" data-testid={`around-network-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-ink">{c.name || "Unknown creator"}</div>
          <div className="text-xs text-ink-soft">{[c.primary_role, c.city].filter(Boolean).join(" · ")}</div>
        </div>
        <Badge variant={c.an_status === "included" ? "default" : "outline"} data-testid={`around-network-status-${c.id}`}>{AN_STATUS_LABELS[c.an_status] || c.an_status}</Badge>
      </div>

      {activityText && (
        <div className="text-xs text-ink-soft">
          <span className="font-medium text-ink">{c.activity_source === "njm" ? "Recent NJM Activity" : "Recent LinkedIn Activity"}:</span> {activityText}
        </div>
      )}

      {c.source_url && (
        <a href={c.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-dim hover:underline" data-testid={`around-network-source-link-${c.id}`}>
          Source <ExternalLink className="h-3 w-3" />
        </a>
      )}

      <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Draft update copy…" className="text-sm" data-testid={`around-network-draft-input-${c.id}`} />

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onEditDraft(c.id, draft)} data-testid={`around-network-save-draft-btn-${c.id}`}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> Save draft
        </Button>
        <Button size="sm" variant={c.an_status === "included" ? "default" : "outline"} disabled={busy} onClick={() => onDecide(c.id, "included")} data-testid={`around-network-include-btn-${c.id}`}>
          Include
        </Button>
        <Button size="sm" variant={c.an_status === "skipped" ? "default" : "outline"} disabled={busy} onClick={() => onDecide(c.id, "skipped")} data-testid={`around-network-skip-btn-${c.id}`}>
          Skip
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(c.id, "removed")} data-testid={`around-network-remove-btn-${c.id}`}>
          Remove
        </Button>
        {onMoveUp && <Button size="sm" variant="ghost" disabled={busy} onClick={() => onMoveUp(c.id)} data-testid={`around-network-move-up-btn-${c.id}`}>↑</Button>}
        {onMoveDown && <Button size="sm" variant="ghost" disabled={busy} onClick={() => onMoveDown(c.id)} data-testid={`around-network-move-down-btn-${c.id}`}>↓</Button>}
      </div>
    </div>
  );
}

export function AroundNetworkSection() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/v1/creative-radar/around-network").then((r) => setCandidates(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const editDraft = (id, draftText) => {
    setBusy(true);
    api.patch(`/v1/creative-radar/around-network/${id}`, { draft_text: draftText })
      .then(() => { toast.success("Draft saved"); load(); })
      .catch(() => toast.error("Couldn't save the draft"))
      .finally(() => setBusy(false));
  };

  const decide = (id, status) => {
    setBusy(true);
    api.post(`/v1/creative-radar/around-network/${id}/decision`, { status })
      .then(() => load())
      .catch(() => toast.error("Couldn't save that decision"))
      .finally(() => setBusy(false));
  };

  const reorder = (newOrderIds) => {
    api.post("/v1/creative-radar/around-network/reorder", { order: newOrderIds }).then(() => load()).catch(() => {});
  };

  const moveUp = (id) => {
    const ids = candidates.map((c) => c.id);
    const i = ids.indexOf(id);
    if (i <= 0) return;
    [ids[i - 1], ids[i]] = [ids[i], ids[i - 1]];
    reorder(ids);
  };
  const moveDown = (id) => {
    const ids = candidates.map((c) => c.id);
    const i = ids.indexOf(id);
    if (i < 0 || i >= ids.length - 1) return;
    [ids[i + 1], ids[i]] = [ids[i], ids[i + 1]];
    reorder(ids);
  };

  const includedCount = candidates.filter((c) => c.an_status === "included").length;

  const publish = () => {
    if (!window.confirm(`Publish Around the Network with ${includedCount} entr${includedCount === 1 ? "y" : "ies"} to The Floor?`)) return;
    setPublishing(true);
    api.post("/v1/creative-radar/around-network/publish")
      .then(() => { toast.success("Published to The Floor"); load(); })
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't publish"))
      .finally(() => setPublishing(false));
  };

  return (
    <div className="space-y-4" data-testid="around-network-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-ink">Around the Network</h2>
        <Button size="sm" disabled={publishing || includedCount === 0} onClick={publish} data-testid="around-network-publish-btn">
          {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
          Publish ({includedCount} included)
        </Button>
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-ink-soft" /></div>}

      {!loading && candidates.length === 0 && (
        <div className="text-sm text-ink-soft" data-testid="around-network-empty-state">NO VERIFIED COMMUNITY ACTIVITY THIS WEEK</div>
      )}

      {!loading && candidates.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {candidates.map((c, i) => (
            <AroundNetworkCard key={c.id} c={c} onEditDraft={editDraft} onDecide={decide}
              onMoveUp={i > 0 ? moveUp : null} onMoveDown={i < candidates.length - 1 ? moveDown : null} busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
}

// =================================================================================================
// Profiles Almost Ready (Wave 5, 2026-11) — read-only, staff-only. No outreach, no automation.
// =================================================================================================

export function ProfilesAlmostReadySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/v1/creative-radar/almost-ready").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="almost-ready-section">
      <h2 className="text-lg font-semibold text-ink">Profiles almost ready</h2>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="rounded-lg border border-hairline bg-surface px-3.5 py-2.5 text-sm" data-testid={`almost-ready-row-${it.id}`}>
            <span className="font-medium text-ink">{it.name || "Unknown"}</span>
            {it.primary_role && <span className="text-ink-soft"> · {it.primary_role}</span>}
            <div className="text-xs text-ink-soft mt-0.5">Missing: {it.missing_elements || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========================================================================================
// Edition Checklist (Wave 6, 2026-11) — include/exclude/reorder the three existing publish
// paths (CotW / Around the Network / the regular weekly-or-monthly radar digest above) and
// fire all of them with one confirmed "Publish edition" action. Never builds its own post —
// every "Publish" below calls the SAME existing endpoint each section above already uses.
// ========================================================================================
export function EditionChecklistSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(() => {
    api.get("/v1/creative-radar/edition-checklist").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (key, included) => {
    api.patch("/v1/creative-radar/edition-checklist", { key, included })
      .then(load)
      .catch((e) => toast.error(e?.response?.data?.detail?.reason || "Couldn't update that block"));
  };

  const move = (idx, dir) => {
    const next = [...items];
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    setItems(next);
    api.post("/v1/creative-radar/edition-checklist/reorder", { order: next.map((b) => b.key) })
      .then(load)
      .catch(() => { toast.error("Couldn't reorder"); load(); });
  };

  const includedCount = items.filter((b) => b.included).length;

  const publish = () => {
    if (includedCount === 0) return;
    if (!window.confirm(`Publish this edition with ${includedCount} block${includedCount === 1 ? "" : "s"} to The Floor? This can't be undone.`)) return;
    setPublishing(true);
    api.post("/v1/creative-radar/edition-checklist/publish")
      .then((r) => {
        const ok = (r.data.results || []).filter((x) => x.ok).length;
        toast.success(`Published ${ok} block${ok === 1 ? "" : "s"} to The Floor`);
        load();
      })
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't publish this edition"))
      .finally(() => setPublishing(false));
  };

  if (loading) return null;

  return (
    <div className="rounded-xl border border-hairline bg-surface p-4 space-y-3" data-testid="edition-checklist-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-ink">Edition checklist</h2>
        <Button onClick={publish} disabled={publishing || includedCount === 0} size="sm" data-testid="edition-checklist-publish-btn">
          {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          Publish edition
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((b, idx) => (
          <div key={b.key} className="flex items-center gap-3 rounded-lg border border-hairline px-3 py-2.5" data-testid={`edition-checklist-item-${b.key}`}>
            <input
              type="checkbox"
              checked={b.included}
              disabled={!b.available}
              onChange={(e) => toggle(b.key, e.target.checked)}
              data-testid={`edition-checklist-checkbox-${b.key}`}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink">{b.label}</div>
              {!b.available && <div className="text-xs text-ink-soft mt-0.5" data-testid={`edition-checklist-reason-${b.key}`}>{b.reason}</div>}
              {b.available && b.preview?.name && <div className="text-xs text-ink-soft mt-0.5">{b.preview.name}</div>}
              {b.available && typeof b.preview?.count === "number" && <div className="text-xs text-ink-soft mt-0.5">{b.preview.count} member{b.preview.count === 1 ? "" : "s"}</div>}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`edition-checklist-up-${b.key}`}>↑</Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} data-testid={`edition-checklist-down-${b.key}`}>↓</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CreativeRadar() {
  const [run, setRun] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [period, setPeriod] = useState("weekly");
  const [freelancers, setFreelancers] = useState([]);
  const [exportData, setExportData] = useState(null);

  useEffect(() => {
    api.get("/v1/build/freelancers").then((r) => setFreelancers(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/v1/creative-radar/runs").then((r) => {
      const latest = (r.data.runs || [])[0];
      if (latest) loadRun(latest.id); else setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRun = useCallback((runId) => {
    setLoading(true);
    api.get(`/v1/creative-radar/runs/${runId}`).then((r) => {
      setRun(r.data.run);
      setCandidates(r.data.candidates || []);
      setExportData(null);
    }).catch(() => toast.error("Couldn't load this run")).finally(() => setLoading(false));
  }, []);

  const generate = () => {
    setGenerating(true);
    api.post("/v1/creative-radar/runs/generate", { period })
      .then((r) => { setRun({ id: r.data.run_id, status: "draft", stats_block: r.data.stats_block, period }); setCandidates(r.data.candidates || []); setExportData(null); })
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't generate a run"))
      .finally(() => setGenerating(false));
  };

  const decide = (candidateId, status) => {
    api.post(`/v1/creative-radar/candidates/${candidateId}/decision`, { status })
      .then(() => setCandidates((prev) => prev.map((c) => (c.id === candidateId ? { ...c, editor_status: status } : c))))
      .catch(() => toast.error("Couldn't save that decision"));
  };

  const editCopy = (candidateId, headline, summary) => {
    api.post(`/v1/creative-radar/candidates/${candidateId}/decision`, { status: candidates.find((c) => c.id === candidateId)?.editor_status || "pending", edited_headline: headline, edited_summary: summary })
      .then(() => setCandidates((prev) => prev.map((c) => (c.id === candidateId ? { ...c, edited_headline: headline, edited_summary: summary } : c))))
      .catch(() => toast.error("Couldn't save the edit"));
  };

  const addManual = (payload) => {
    api.post(`/v1/creative-radar/runs/${run.id}/manual-candidate`, payload)
      .then((r) => setCandidates((prev) => [...prev, r.data]))
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't add that member"));
  };

  const included = candidates.filter((c) => c.editor_status === "included");

  const publish = () => {
    if (!window.confirm(`Publish this edition with ${included.length} member${included.length === 1 ? "" : "s"} to The Floor? This can't be undone.`)) return;
    setPublishing(true);
    api.post(`/v1/creative-radar/runs/${run.id}/publish`)
      .then(() => { toast.success("Published to The Floor"); loadRun(run.id); })
      .catch((e) => toast.error(e?.response?.data?.detail?.code || "Couldn't publish"))
      .finally(() => setPublishing(false));
  };

  const loadExport = () => {
    api.get(`/v1/creative-radar/runs/${run.id}/export`).then((r) => setExportData(r.data)).catch(() => toast.error("Export not available"));
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="creative-radar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold text-ink flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-brand" /> Creative Radar
          </h1>
          <p className="text-sm text-ink-soft mt-1">Curated community digest — AI drafts copy, you decide what ships.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="radar-period-select"
            className="rounded-lg border border-hairline bg-surface2 px-3 py-2 text-sm text-ink">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <Button onClick={generate} disabled={generating} data-testid="radar-generate-btn">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Generate {period} edition
          </Button>
        </div>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-ink-soft" /></div>}

      <div className="mb-6">
        <EditionChecklistSection />
      </div>

      <div className="mb-6">
        <CreatorOfTheWeekSection />
      </div>

      <div className="mb-6">
        <AroundNetworkSection />
      </div>

      <div className="mb-6">
        <ProfilesAlmostReadySection />
      </div>

      {!loading && !run && (
        <EmptyState title="No draft yet" hint="Generate a weekly or monthly edition to start reviewing candidates." testId="radar-empty-state" />
      )}

      {!loading && run && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Badge variant={run.status === "published" ? "default" : "outline"} data-testid="radar-run-status">{run.status}</Badge>
            {run.status === "published" && <span className="text-xs text-ink-soft">Published {run.published_at ? new Date(run.published_at).toLocaleString() : ""}</span>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <StatCard label="New Collabs" value={run.stats_block?.new_collaborations_started ?? 0} />
            <StatCard label="Applications" value={run.stats_block?.collaboration_applications_submitted ?? 0} />
            <StatCard label="Assignments Done" value={run.stats_block?.assignments_completed ?? 0} />
          </div>

          {run.status === "draft" && <ManualAddForm freelancers={freelancers} onAdd={addManual} />}

          <div className="space-y-3">
            {candidates.map((c) => (
              <CandidateCard key={c.id} c={c} onDecide={decide} onEdit={editCopy} />
            ))}
          </div>

          {run.status === "draft" && (
            <div className="flex items-center gap-3 pt-2">
              <Button size="lg" onClick={publish} disabled={publishing || included.length === 0} data-testid="radar-publish-btn">
                {publishing && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                Publish ({included.length} included)
              </Button>
            </div>
          )}

          {run.status === "published" && (
            <div className="pt-2">
              <Button variant="outline" onClick={loadExport} data-testid="radar-export-btn">View structured export</Button>
              {exportData && (
                <pre className="mt-3 rounded-xl border border-hairline bg-surface2 p-4 text-xs text-ink overflow-auto max-h-96" data-testid="radar-export-json">
                  {JSON.stringify(exportData, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
